import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';

import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Stack,
  Avatar,
  Badge,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  VerifiedUser as VerifiedUserIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

import { LoadingPage } from '../../components/ui/LoadingSpinner';
import DataTable from '../../components/ui/DataTable';
import { formatDate, getRoleDisplayName, debounce } from '../../lib/utils';
import { toast } from 'react-hot-toast';
import apiClient from '../../lib/api';
import { normalizeListPagination } from '../../lib/normalizeListPagination';

const columns = [
  {
    id: 'firstName',
    label: 'User',
    render: (value, row) => `${row.firstName} ${row.lastName}`,
  },
  {
    id: 'type',
    label: 'Type',
    render: (value, row) => {
      if (!row.type) return <span className="text-gray-400">-</span>;
      
      const typeColors = {
        'admin': 'bg-blue-100 text-blue-800',
        'funeral_director': 'bg-green-100 text-green-800',
        'designer': 'bg-purple-100 text-purple-800',
        'staff': 'bg-orange-100 text-orange-800',
      };
      
      const colorClass = typeColors[row.type] || 'bg-gray-100 text-gray-800';
      const displayName = row.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
          {displayName}
        </span>
      );
    },
  },
  {
    id: 'roles',
    label: 'Roles',
    render: (value, row) => (
      <div className="flex flex-wrap gap-2">
        {row.roles?.map((userRole) => (
          <Chip 
            key={userRole.id} 
            label={getRoleDisplayName(userRole.role.name)}
            size="small"
            color="primary"
            variant="outlined"
          />
        ))}
      </div>
    ),
  },
  {
    id: 'email',
    label: 'Email',
  },
  {
    id: 'emailVerifiedAt',
    label: 'Email Verified',
    render: (value, row) => (
      <span className={`badge ${row.emailVerifiedAt ? 'badge-success' : 'badge-warning'}`}>
        {row.emailVerifiedAt ? 'Verified' : 'Unverified'}
      </span>
    ),
  },
  {
    id: 'phone',
    label: 'Phone',
  },
  {
    id: 'isActive',
    label: 'Status',
    render: (value, row) => (
      <span className={`badge ${row.isActive ? 'badge-success' : 'badge-warning'}`}>
        {row.isActive ? 'Active' : 'Inactive'}
      </span>
    ),
  },
  {
    id: 'createdAt',
    label: 'Joined',
    render: (value, row) => formatDate(row.createdAt),
  },
];

const statusOptions = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const verificationOptions = [
  { value: 'true', label: 'Verified' },
  { value: 'false', label: 'Unverified' },
];

const filters = [
  {
    key: 'search',
    label: 'Search',
    type: 'text',
    placeholder: 'Search by name or email...'
  },
  {
    key: 'isActive',
    label: 'Status',
    type: 'select',
    options: statusOptions,
  },
  {
    key: 'emailVerified',
    label: 'Email Verified',
    type: 'select',
    options: verificationOptions,
  },
];

const AdminUsersPage = () => {
  const navigate = useNavigate();
  
  // State for users data
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  
  // State for loading and actions
  const [isLoading, setIsLoading] = useState(true);

  // Load users data
  const loadUsers = async (searchParams) => {
    try {
      setIsLoading(true);
      
      const params = {
        page: (searchParams?.page ?? 1),
        limit: searchParams?.rowsPerPage ?? 10,
        type: 'staff', // Only show staff users - admins, funeral directors, and designers are managed elsewhere
      };

      // Add search and filter parameters
      if (searchParams?.searchTerm) {
        params.search = searchParams.searchTerm;
      }
      if (searchParams?.filterValues) {
        const filters = searchParams.filterValues;
        if (filters.isActive && filters.isActive !== 'all') {
          params.isActive = filters.isActive === 'true';
        }
        if (filters.emailVerified && filters.emailVerified !== 'all') {
          params.emailVerified = filters.emailVerified === 'true';
        }
      }
      
      const response = await apiClient.getUsers(params);
      console.log('API response:', response);
      // Filter out admins, funeral directors, and designers - only show staff users
      const filteredUsers = (response.data || []).filter(
        user => user.type !== 'admin' && user.type !== 'funeral_director' && user.type !== 'designer'
      );
      setUsers(filteredUsers);
      setPagination(normalizeListPagination(response, { limit: 10 }));
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
      setUsers([]);
      setPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user actions
  const handleRowSelect = (user) => {
    navigate(`/users/${user.id}`);
  };

  const handleSearch = (searchParams) => {
    console.log('Search params:', searchParams);
    loadUsers(searchParams);
  };

  const handleAddUser = () => {
    navigate('/users/add');
  };

  // Load data on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Remove the full-page loading state that causes unmounting
  // Instead, show loading state within the DataTable component

  return (
    <DashboardLayout title="User Management">
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        User Management
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 2 
        }}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4a8f 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Platform Users
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {pagination.total}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #d885e8 0%, #e04a5f 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Active Users
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {users.filter(u => u.isActive).length}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #4a9ee8 0%, #00d8e0 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <VerifiedUserIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Verified Users
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {users.filter(u => u.emailVerifiedAt).length}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #3dd06f 0%, #32e0c0 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  New This Month
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {users.filter(u => {
                    const created = new Date(u.createdAt);
                    const now = new Date();
                    return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
                  }).length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Users Table */}
      <Box sx={{ mt: 4 }}>         
        <DataTable
          data={users}
          title="Users"
          onAdd={handleAddUser}
          addButtonText="Add User"
          columns={columns}
          filters={filters}
          onSelect={handleRowSelect}
          onSearch={handleSearch}
          totalPages={pagination.totalPages}
          currentPage={pagination.page}
          loading={isLoading}
          onRowClick={(row) => navigate(`/users/${row.id}`)}
        />
      </Box>
    </DashboardLayout>
  );
};

export default AdminUsersPage;
